import Cookies from 'js-cookie';
import throttle from 'lodash.throttle';
import { Button, Navbar as DSNavbar } from 'lucid-components-react';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useSiteMetadata } from '@lucid/gatsby-theme-lucid';

import LayoutContext, {
  LayoutContextProps
} from '../../contexts/layoutContext';
import useNavButtons from '../../hooks/use-nav-buttons';
import { MenuLink } from '../../types/types';
import Link from '../link/link';
import './navbar.scss';

const LOGO =
  'https://d2slcw3kip6qmk.cloudfront.net/marketing/images/LucidForEducationWebsite.svg';

export interface LinkInterface extends MenuLink {
  primary?: boolean;
  secondary?: boolean;
}

interface Props {
  alwaysScrolled?: boolean;
  menu: LinkInterface[];
  scrollDistance?: number;

  isHomepage?: boolean;
  isStickyMobile?: boolean;
}

const Navbar: FC<Props> = ({ alwaysScrolled, menu, scrollDistance = 0 }) => {
  const {
    language: { langPrefix, urlLangPrefix },
    marketo: { getMarketoModal, getMarketoFields }
  } = useContext<LayoutContextProps>(LayoutContext);

  const { title } = useSiteMetadata();

  alwaysScrolled = true;

  const LOGO_LINK = `/${urlLangPrefix}`;

  const isBrowser = typeof window !== 'undefined';
  const username = isBrowser
    ? window.atob(Cookies.get('username') || '')
    : undefined;

  const [currentButtons, updateButtons] = useNavButtons();
  const [buttons, setButtons] = useState(currentButtons);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // @ts-ignore
    setButtons(updateButtons(username));
    const handleScroll = throttle(() => {
      const distance = scrollDistance;
      setIsScrolled(window.pageYOffset > distance);
    }, 100);

    if (scrollDistance) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderLink = (
    link: LinkInterface,
    isSubmenuLink?: boolean,
    isButton?: boolean
  ) => (
    <Link
      asButton={isButton}
      block
      key={link.link.url!.path}
      primary={link.primary}
      secondary={link.secondary}
      style={{ width: '100%' }}
      to={link.link.url!.path}
      underline={isSubmenuLink ? 'hover' : 'none'}
    >
      {link.title}
    </Link>
  );

  const renderDropdown = (menuItem: LinkInterface, isButton?: boolean) => {
    return (
      <DSNavbar.Item>
        <DSNavbar.Dropdown
          toggle={toggleHandler => (
            <Button
              block
              asLink={!isButton}
              className={`${
                menuItem.truncate ? 'navbar__button--truncate' : ''
              }`}
              primary={menuItem.primary}
              secondary={menuItem.secondary}
              style={{ position: 'relative' }}
              onMouseOver={toggleHandler}
              onFocus={toggleHandler}
            >
              {menuItem.title}
            </Button>
          )}
          css={{
            position: 'relative'
          }}
        >
          <DSNavbar.DropdownContainer>
            {menuItem.links.map(dropdownLink => renderLink(dropdownLink, true))}
          </DSNavbar.DropdownContainer>
        </DSNavbar.Dropdown>
      </DSNavbar.Item>
    );
  };

  const renderItem = (
    link: LinkInterface,
    isSubmenuLink?: boolean,
    isButton?: boolean
  ) => {
    if (link.links.length) {
      return renderDropdown(link, isButton);
    }
    if (link.fieldMarketo) {
      const { mktoId, mktoFields } = getMarketoFields(
        link.fieldMarketo,
        langPrefix
      );
      if (mktoId) {
        return (
          <DSNavbar.Item key={link.link.url!.path}>
            <Button asLink onClick={() => getMarketoModal(mktoId, mktoFields)}>
              {link.title}
            </Button>
          </DSNavbar.Item>
        );
      }
    }
    return (
      <DSNavbar.Item key={link.link.url!.path}>
        {renderLink(link, isSubmenuLink, isButton)}
      </DSNavbar.Item>
    );
  };

  return (
    <DSNavbar sticky>
      <div className="navbar__container">
        <DSNavbar.Container>
          <DSNavbar.Brand>
            <Link to={LOGO_LINK}>
              <img className="navbar__logo-image" src={LOGO} alt={title} />
            </Link>
          </DSNavbar.Brand>
          <DSNavbar.Toggle>Menu</DSNavbar.Toggle>
          <DSNavbar.InnerContainer>
            {menu.map(item => renderItem(item))}
            {buttons.map(buttonLink => renderItem(buttonLink, false, true))}
          </DSNavbar.InnerContainer>
          <DSNavbar.Border isHidden={alwaysScrolled ? false : !isScrolled} />
        </DSNavbar.Container>
      </div>
    </DSNavbar>
  );
};

export default Navbar;
